import { computed, effect, inject, Injectable, Injector, signal, untracked } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { DocumentTypeEnum, FeatureFlagController, UserController } from '@domain';
import { environment } from '@environments';
import { filter, firstValueFrom, timer } from 'rxjs';
import { EditDataService } from './_edit-data.service';
import { EditService } from './edit-service';
import { TemplateEditService } from './template-edit.service';

/** Os dois modos do editor (ver docs/EDITOR-CHAT-IA.md, seção 2). */
export type EditMode = 'manual' | 'ai';

/** Por que o modo IA não está disponível neste documento (vira o tooltip do rail). */
export type AiModeUnavailableReason = 'type' | 'template' | null;

/** Flag da feature: sem ela liberada o rail nem aparece. */
export const AI_EDIT_FEATURE_FLAG = 'ai_edit_mode';

/** Duração mínima da transição entre modos: 2 s OU até o modo carregar, o que demorar mais. */
export const EDIT_MODE_TRANSITION_MS = 2000;

/** Liga a feature na máquina de quem desenvolve, onde o mock da API está ativo. */
const LOCAL_FEATURE_OVERRIDE_KEY = 'ai_edit_mode';

const isEditMode = (value: unknown): value is EditMode => value === 'manual' || value === 'ai';

/**
 * A feature "Editar com IA" está liberada para este usuário? Onde o environment não exige a
 * flag (`AI_EDIT_REQUIRE_FLAG: false`, hoje local/dev/hom) está para todos; na máquina de
 * quem desenvolve, com o mock da API, também vale o override do localStorage.
 *
 * Função solta (e não só o `computed` do serviço) para quem decide ANTES de abrir o editor,
 * como o wizard "Criar com IA", não precisar instanciar os serviços do editor.
 */
export function isAiEditFeatureEnabled(featureFlags: FeatureFlagController): boolean {
    return (
        !environment.AI_EDIT_REQUIRE_FLAG ||
        featureFlags.isReleased(AI_EDIT_FEATURE_FLAG) ||
        readLocalFeatureOverride()
    );
}

function readLocalFeatureOverride(): boolean {
    if (!environment.AI_EDIT_MOCK) return false;

    try {
        return localStorage.getItem(LOCAL_FEATURE_OVERRIDE_KEY) === 'on';
    } catch {
        return false;
    }
}

/**
 * Modo do editor (manual × IA): qual está na tela, a transição entre eles, a URL
 * (`?mode=ai|manual`) e o último modo usado, guardado por usuário.
 *
 * `providedIn: 'root'` como os demais serviços do editor; o `EditPage` chama `init` ao
 * abrir e `clear` ao sair.
 */
@Injectable({ providedIn: 'root' })
export class EditModeService {
    // * [Injects]
    private _router = inject(Router);
    private _injector = inject(Injector);
    private _featureFlags = inject(FeatureFlagController);
    private _userController = inject(UserController);
    private _editData = inject(EditDataService);
    private _editService = inject(EditService);
    private _templateEdit = inject(TemplateEditService);

    // * [State]
    private _mode = signal<EditMode>('manual');
    /** Modo na tela. */
    public mode = this._mode.asReadonly();

    private _switchingTo = signal<EditMode | null>(null);
    /** Destino de uma troca em andamento (o overlay de transição aparece enquanto não é null). */
    public switchingTo = this._switchingTo.asReadonly();

    public isAiMode = computed(() => this._mode() === 'ai');

    /** Rota de onde a URL é sincronizada (a do editor aberto). */
    private _route: ActivatedRoute | null = null;

    /**
     * Feature liberada para este usuário. Sem ela o editor fica exatamente como era: sem
     * rail e sem `?mode=` na URL. Onde o environment não exige a flag
     * (`AI_EDIT_REQUIRE_FLAG: false`, hoje local/dev/hom), está liberada para todos.
     */
    public featureEnabled = computed(() => isAiEditFeatureEnabled(this._featureFlags));

    /** O MVP só edita documentos comuns, e nunca o rascunho de um modelo. */
    public aiUnavailableReason = computed<AiModeUnavailableReason>(() => {
        if (this._templateEdit.isTemplateMode()) return 'template';

        const type = this._editData.document()?.type;
        if (type && type !== DocumentTypeEnum.NORMAL) return 'type';

        return null;
    });

    public aiAvailable = computed(
        () => this.featureEnabled() && this.aiUnavailableReason() === null
    );

    /** Os dados do modo de destino estão prontos (documento carregado, nada em carregamento). */
    private _ready = computed(() => !!this._editData.document() && !this._editService.loading());

    constructor() {
        // Documento abriu num modo que ele não suporta (link com ?mode=ai para um contrato,
        // ou o último modo era IA): volta ao manual em silêncio, sem transição.
        effect(() => {
            const document = this._editData.document();
            if (!document || this._mode() !== 'ai' || this.aiAvailable()) return;

            untracked(() => {
                this._mode.set('manual');
                this._syncUrl('manual');
            });
        });
    }

    /**
     * Resolve o modo inicial: `?mode=` da URL, senão o último usado por este usuário, senão
     * o manual. Sem a feature liberada é sempre o manual.
     */
    public init(route: ActivatedRoute): void {
        this._route = route;
        this._switchingTo.set(null);

        if (!this.featureEnabled()) {
            this._mode.set('manual');
            return;
        }

        const fromUrl = route.snapshot.queryParams['mode'];
        const initial: EditMode = isEditMode(fromUrl) ? fromUrl : (this._readStored() ?? 'manual');

        this._mode.set(initial);
        this._syncUrl(initial);
    }

    /**
     * Troca de modo com a transição: o overlay fica na tela por 2 s ou até o modo de
     * destino ter os dados, o que demorar mais. Pedidos repetidos durante a troca são
     * ignorados.
     */
    public async switchTo(mode: EditMode): Promise<void> {
        if (mode === this._mode() || this._switchingTo()) return;
        if (mode === 'ai' && !this.aiAvailable()) return;

        this._switchingTo.set(mode);
        try {
            await Promise.all([firstValueFrom(timer(EDIT_MODE_TRANSITION_MS)), this._whenReady()]);
        } finally {
            // A tela pode ter sido fechada no meio da transição (clear zerou o destino).
            if (this._switchingTo() === mode) {
                this._mode.set(mode);
                this._store(mode);
                this._syncUrl(mode);
                this._switchingTo.set(null);
            }
        }
    }

    /** Saída do editor: o próximo documento resolve o modo de novo. */
    public clear(): void {
        this._route = null;
        this._switchingTo.set(null);
        this._mode.set('manual');
    }

    private _whenReady(): Promise<unknown> {
        if (this._ready()) return Promise.resolve(true);

        return firstValueFrom(
            toObservable(this._ready, { injector: this._injector }).pipe(filter(Boolean))
        );
    }

    private _syncUrl(mode: EditMode): void {
        const route = this._route;
        if (!route || !this.featureEnabled()) return;
        if (route.snapshot.queryParams['mode'] === mode) return;

        this._router.navigate([], {
            relativeTo: route,
            queryParams: { mode },
            queryParamsHandling: 'merge',
            replaceUrl: true,
        });
    }

    private get _storageKey(): string | null {
        try {
            const userId = this._userController.getCurrentUserId();
            return userId ? `editMode:${userId}` : null;
        } catch {
            return null;
        }
    }

    private _readStored(): EditMode | null {
        const key = this._storageKey;
        if (!key) return null;

        try {
            const value = localStorage.getItem(key);
            return isEditMode(value) ? value : null;
        } catch {
            return null; // storage bloqueado (janela privada): segue sem lembrar
        }
    }

    private _store(mode: EditMode): void {
        const key = this._storageKey;
        if (!key) return;

        try {
            localStorage.setItem(key, mode);
        } catch {
            // storage bloqueado: o modo vale só nesta visita
        }
    }
}
